import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SnackbarService } from '@digilize/shared/feature/modules/snackbar';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarType } from '@shared/definitions';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

export const SKIP_ERROR_INTERCEPTOR = new HttpContextToken<boolean>(() => false);

export interface ApiError {
  type: string;
  message?: string;
  info: ApiErrorInfo;
  request_id?: string;
}

export interface ApiErrorInfo {
  field: string;
  kind: ErrorKind;
  message: string; // detailed message should be used if provided
}

export enum ErrorKind {
  vacancy_match_no_work_experience = 'vacancy_match_no_work_experience',
}

export const EXCEPTIONAL_ERROR_KINDS: ErrorKind[] = [ErrorKind.vacancy_match_no_work_experience];

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private snackbarService: SnackbarService,
    private translateService: TranslateService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((httpError: HttpErrorResponse) => {
        if (req.context.get(SKIP_ERROR_INTERCEPTOR)) {
          return throwError(() => httpError);
        }

        const apiError: ApiError = httpError?.error?.error;
        if (httpError.status === 400 && !this.isException(apiError?.info)) {
          const message = getErrorMessage(apiError, (key) => this.translateService.instant(key));
          if (message) {
            this.snackbarService.openSnackbar(SnackbarType.Error, {
              duration: 10000,
              data: { msg: message, translate: false },
            });
          }
        }
        return throwError(() => httpError);
      })
    );
  }

  isException(info: ApiErrorInfo | ErrorKind): boolean {
    if (this.isErrorKind(info)) {
      return EXCEPTIONAL_ERROR_KINDS.includes(info);
    } else if (this.isInfoObject(info)) {
      return EXCEPTIONAL_ERROR_KINDS.includes(info.kind);
    }
    return false;
  }

  isErrorKind(info: ApiErrorInfo | ErrorKind): info is ErrorKind {
    return typeof info === 'string';
  }

  isInfoObject(info: ApiErrorInfo | ErrorKind): info is ApiErrorInfo {
    return typeof info === 'object' && info !== null && 'kind' in info;
  }
}
export function getErrorMessage(apiError: ApiError, translateFn: (key: string) => string): string | undefined {
  if (!apiError?.type) {
    return undefined;
  }

  const backendMessage = apiError.info?.message;
  if (backendMessage && apiError.info?.field) {
    const fieldLabel = translateFn(`form.field.${apiError.info?.field}`);
    return backendMessage.replace('{field}', fieldLabel);
  } else if (backendMessage) {
    return backendMessage;
  } else {
    return apiError.message;
  }
}
